import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { classNames, useTranslate } from "helpers";

// Hooks
import { useHandleTextFieldChange } from "services/forms/hooks";

// UI
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { TextField } from "ui/components/TextField/TextField";

// Local

import { Dialog } from "ui/components/Dialog";
import { ReactSVG } from "react-svg";

import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from "@dnd-kit/core";

import {
    SortableContext,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import SortableItem from "ui/components/SortableItem/SortableItem";
import { ContractRentTemplateEditCardProps } from "./types";

import { Errors } from "services/forms/validation/types";

import ContractRentTemplateItemEditCard from "../ContractRentTemplateItemEditCard/ContractRentTemplateItemEditCard";
import { NoResults } from "ui/components/NoResults";
import { validateYup } from "services/forms/validation/validation";
import { CONTRACT_RENT_CONTENT_TEMPLATE_EDIT_FORM_SCHEMA } from "./validation";
import { Alert } from "ui/components/Alert";

import { initialContractRentTemplateForm, initialContractRentTemplateItemForm } from "services/api/settings/documents/consts";
import { createContractRentTemplateAPI, getContractRentTemplateAPI, updateContractRentTemplateAPI } from "services/api/settings/documents/api";
import { ContractRentTemplateFormValues, ContractRentTemplateItemFormValues } from "services/api/settings/documents/types";



const ContractRentTemplateEditCard: React.FC<ContractRentTemplateEditCardProps> = (props) => {
    const { id, onSuccess, onClose } = props;

    const { t } = useTranslate();

    const [showEditModal, setShowEditModal] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<Errors>({});

    const [editingItem, setEditingItem] =
        useState<ContractRentTemplateItemFormValues | null>(null);

    const [formValues, setFormValues] = useState<ContractRentTemplateFormValues>(
        initialContractRentTemplateForm
    );
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        })
    );

    useEffect(() => {
        if (id) fetchContractRentTemplate(id);
    }, [id]);

    const fetchContractRentTemplate = async (id: string) => {
        if (id === undefined) {
            setFormValues(initialContractRentTemplateForm);
            return;
        }

        setIsLoading(true);

        let result;
        try {
            result = await getContractRentTemplateAPI(id);
        } catch {
            setIsLoading(false);
            return;
        }

        setFormValues(result);
        setIsLoading(false);

        setErrors({});
    };

    const handleCreateItemClick = useCallback(() => {
        setEditingItem(initialContractRentTemplateItemForm);
        setShowEditModal(true);
    }, []);

    const handleEditItemClick = (
        e: React.MouseEvent,
        id: string
    ) => {
        e.preventDefault();
        e.stopPropagation();

        const item = formValues.items.find(i => i.id === id);
        if (!item) return;

        setEditingItem(item);
        setShowEditModal(true);
    };

    const handleItemAddSuccess = useCallback(
        (item: ContractRentTemplateItemFormValues) => {
            setFormValues(prev => {
                if (!prev) return prev;

                const items = prev.items ?? [];
                const exists = items.some(i => i.id === item.id);

                return {
                    ...prev,
                    items: exists
                        ? items.map(i => (i.id === item.id ? item : i))
                        : [...items, { ...item, id: crypto.randomUUID(), isNew: true }],

                };
            });

            setShowEditModal(false);
            setEditingItem(null);
        },
        [setFormValues]
    );

    const handleDeleteItemClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();

        const item = formValues.items.find(i => i.id === id);
        if (!item) return;

        setEditingItem(item);
        setOpenDialog(true);
    }

    const handleDeleteItemDialogConfirm = useCallback(() => {
        if (!editingItem?.id) return;

        setFormValues(prev => {
            if (!prev) return prev;

            return {

                ...prev,
                items: prev.items.map(item =>
                    item.id === editingItem.id
                        ? { ...item, deleted: true }
                        : item
                ),

            };
        });

        setOpenDialog(false);
        setEditingItem(null);
    }, [editingItem, setFormValues]);


    const handleDeleteItemDialogClose = useCallback(() => {
        setEditingItem(null);
        setOpenDialog(false);
    }, []);


    const handleDragEnd = useCallback((event: any) => {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        setFormValues(prev => {
            if (!prev) return prev;

            const items = prev.items.filter(i => !i.deleted);

            const oldIndex = items.findIndex(i => i.id === active.id);
            const newIndex = items.findIndex(i => i.id === over.id);

            const reordered = arrayMove(items, oldIndex, newIndex);

            return {

                ...prev,
                items: reordered,

            };
        });
    }, [setFormValues]);


    const handleFormSubmit = async (e: any) => {
        e.preventDefault();

        const isValid = validateYup(
            formValues,
            CONTRACT_RENT_CONTENT_TEMPLATE_EDIT_FORM_SCHEMA,
            setErrors
        );

        if (!isValid) {
            return;
        }

        setIsLoading(true);

        try {
            let result;
            if (!!id) {
                result = await updateContractRentTemplateAPI(id, formValues, t);
            } else {
                result = await createContractRentTemplateAPI(formValues, t);
            }
            if (result && onSuccess) onSuccess();
        } catch (e) {
            setIsLoading(false);
            return;
        }

        setIsLoading(false);
    };

    const handleTextFieldChange = useHandleTextFieldChange(
        setFormValues,
        formValues
    );

    const items = formValues.items.filter(i => !i.deleted);


    return (
        <>
            <div className="flex gap-4">
                <div className="w-full m-auto">
                    <div className="flex gap-3 h-full">
                        <form noValidate className="text-left flex-1 ">
                            <div className="p-4">
                                <div className="mb-3">
                                    <TextField
                                        type="text"
                                        name="name"
                                        label={t("admin.fleet.vehicle.contract.add.content.name")}
                                        placeholder={t(
                                            "admin.fleet.vehicle.contract.add.content.name.placeholder"
                                        )}
                                        onChange={handleTextFieldChange}
                                        value={formValues.name}
                                        error={errors.name}
                                        required
                                    />
                                </div>
                                <div className={classNames("bg-white  rounded-sm mb-3", items.length > 0 ? "border border-slate-200" : "")}>
                                    <div className="divide-y divide-slate-200">
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext
                                                items={items.map(i => i.id!)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                <div className="divide-y divide-slate-200">
                                                    {items.map(item => (
                                                        <SortableItem key={item.id} id={item.id!}>
                                                            <div
                                                                className="pl-4 pr-2 py-4 flex justify-between gap-2 hover:bg-slate-50 cursor-pointer"
                                                                onClick={(e) => handleEditItemClick(e, item.id!)}
                                                            >
                                                                <div>
                                                                    <div className="text-sm font-medium mb-2" dangerouslySetInnerHTML={{ __html: item.title ?? "" }} />
                                                                    <div className="text-sm" dangerouslySetInnerHTML={{ __html: item.content ?? "" }} />
                                                                </div>
                                                                <div className="min-w-[50px] flex items-center justify-center">
                                                                    <span
                                                                        onClick={(e) => handleDeleteItemClick(e, item.id!)}
                                                                        className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-full"
                                                                    >
                                                                        <ReactSVG src="/media/icons/trash.svg" className="w-4 h-4 stroke-[#bd0000]" />
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </SortableItem>
                                                    ))}
                                                </div>
                                            </SortableContext>
                                        </DndContext>

                                    </div>
                                </div>
                                <div className="flex flex-col mb-3">
                                    {items.length === 0 && (
                                        <NoResults
                                            title={t("admin.fleet.vehicle.contract.add.content.noresults.title")}
                                            message={t("admin.fleet.vehicle.contract.add.content.noresults.message")}
                                        />
                                    )}
                                    <div className={classNames("flex ", items.length === 0 ? "justify-center" : "justify-end")}>
                                        <Button
                                            variant="primary"
                                            startIcon="/media/icons/plus.svg"
                                            onClick={handleCreateItemClick}
                                        >
                                            {t("admin.fleet.vehicle.contract.add.content.button")}
                                        </Button>
                                    </div>
                                </div>
                                {errors && errors["items"] !== undefined && items.length === 0 ? (
                                    <Alert title="Važno!" message={errors["items"]} variant="danger" />
                                ) : <></>}
                            </div>
                            <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                                <Button
                                    variant="outlined"
                                    children={t("admin.close")}
                                    onClick={() => onClose()}
                                />
                                <Button
                                    variant="primary"
                                    children={t("admin.save")}
                                    onClick={handleFormSubmit}
                                />
                            </div>

                        </form>
                    </div>
                </div>
            </div>
            <Modal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                position="centered"
                size="medium"
            >
                <ModalHeader
                    icon="/media/images/add-icon.svg"
                    title={
                        editingItem?.id
                            ? t("admin.fleet.vehicle.contract.add.content.edit.title")
                            : t("admin.fleet.vehicle.contract.add.content.add.title")
                    }
                    onHide={() => setShowEditModal(false)}
                />
                <ModalBody>
                    {editingItem && (
                        <ContractRentTemplateItemEditCard
                            item={editingItem}
                            onSuccess={handleItemAddSuccess}
                            onClose={() => setShowEditModal(false)}
                        />
                    )}
                </ModalBody>
            </Modal>
            <Dialog
                open={openDialog}
                title={t(
                    "admin.fleet.vehicle.contract.add.content.item.dialog.delete.title"
                )}
                message={t(
                    "admin.fleet.vehicle.contract.add.content.item.dialog.delete.message"
                )}
                confirmText={t(
                    "admin.fleet.vehicle.contract.add.content.item.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.fleet.vehicle.contract.add.content.item.dialog.delete.canceltext"
                )}
                onConfirm={handleDeleteItemDialogConfirm}
                onCancel={handleDeleteItemDialogClose}
            />
        </>
    );
};
export default memo(ContractRentTemplateEditCard);
